import { useEffect, useState } from 'react';
import keycloak from '../auth/keycloak';
import './TokenPurchaseModal.css';

type PricingTab = 'individual' | 'professional';

interface TokenPurchaseLabels {
  title: string;
  individual: string;
  professional: string;
  close: string;
  configurationError: string;
}

interface TokenPurchaseModalProps {
  isOpen: boolean;
  labels: TokenPurchaseLabels;
  onClose: () => void;
}

const stripeData = {
  individualTableId:
    process.env.REACT_APP_STRIPE_PRICING_TABLE_ID_INDIVIDUAL || '',
  professionalTableId:
    process.env.REACT_APP_STRIPE_PRICING_TABLE_ID_PROFESSIONAL || '',
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
};

const STRIPE_SCRIPT_URL = 'https://js.stripe.com/v3/pricing-table.js';

const TokenPurchaseModal = ({
  isOpen,
  labels,
  onClose,
}: TokenPurchaseModalProps) => {
  const [activeTab, setActiveTab] = useState<PricingTab>('individual');

  useEffect(() => {
    if (!document.querySelector(`script[src="${STRIPE_SCRIPT_URL}"]`)) {
      const script = document.createElement('script');
      script.src = STRIPE_SCRIPT_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const tableId =
    activeTab === 'individual'
      ? stripeData.individualTableId
      : stripeData.professionalTableId;
  const isConfigured = Boolean(
    tableId && stripeData.publishableKey && keycloak.tokenParsed?.sub,
  );

  return (
    <div
      className="token-purchase-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="token-purchase-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="token-purchase-title"
      >
        <button
          type="button"
          className="token-purchase-close"
          onClick={onClose}
          aria-label={labels.close}
          title={labels.close}
        >
          ×
        </button>

        <header className="token-purchase-header">
          <h2 id="token-purchase-title">{labels.title}</h2>
          <div className="token-pricing-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'individual'}
              className={activeTab === 'individual' ? 'active' : ''}
              onClick={() => setActiveTab('individual')}
            >
              {labels.individual}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'professional'}
              className={activeTab === 'professional' ? 'active' : ''}
              onClick={() => setActiveTab('professional')}
            >
              {labels.professional}
            </button>
          </div>
        </header>

        <div className="token-pricing-content">
          {isConfigured ? (
            <stripe-pricing-table
              key={tableId}
              pricing-table-id={tableId}
              publishable-key={stripeData.publishableKey}
              client-reference-id={keycloak.tokenParsed?.sub}
            />
          ) : (
            <p className="token-pricing-error">{labels.configurationError}</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default TokenPurchaseModal;
